import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, CalendarPlus, Trash2, Download } from "lucide-react";
import { toast } from "sonner";
import { api, apiDownload, formatPrice } from "../lib/api";
import { useT } from "../lib/i18n";
import { PageHeader } from "../components/PageHeader";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const Bezichtigingen = () => {
  const { t, lang } = useT();
  const STATUS = {
    gepland: { label: t("p2.vst.gepland"), cls: "bg-brandblue-light text-brandblue" },
    bezocht: { label: t("p2.vst.bezocht"), cls: "bg-success/15 text-success" },
    afgewezen: { label: t("p2.vst.afgewezen"), cls: "bg-danger/15 text-danger" },
    geannuleerd: { label: t("p2.vst.geannuleerd"), cls: "bg-slate-100 text-slate-500" },
  };
  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString(lang === "nl" ? "nl-NL" : "en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };
  const [items, setItems] = useState(null);

  const load = () => api("/viewings").then((d) => setItems(d.viewings)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => { await api(`/viewings/${id}`, { method: "PATCH", body: JSON.stringify({ status }) }); load(); };
  const remove = async (id) => { await api(`/viewings/${id}`, { method: "DELETE" }); load(); };
  const ical = async (id) => {
    try { await apiDownload(`/viewings/${id}/ical`, `viewing-${id}.ics`); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div>
      <PageHeader icon={CalendarClock} title={t("p2.view.title")} subtitle={t("p2.view.subtitle")} />
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        {items === null ? (
          <div className="space-y-3">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-28 animate-pulse rounded-2xl bg-slate-100" />)}</div>
        ) : items.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-border p-16 text-center" data-testid="viewings-empty">
            <CalendarPlus className="mx-auto text-slate-300" size={44} />
            <p className="mt-4 text-slate-500">{t("p2.view.empty")}</p>
            <Button asChild className="mt-6 rounded-xl bg-brandblue hover:bg-brandblue/90"><Link to="/woningen">{t("p2.reacties.viewHomes")}</Link></Button>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((v) => {
              const s = v.listing_summary || {};
              const st = STATUS[v.status] || STATUS.gepland;
              return (
                <div key={v.id} data-testid={`viewing-${v.id}`} className="rounded-2xl border border-border bg-white p-4 shadow-sm">
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="h-16 w-20 shrink-0 overflow-hidden rounded-xl bg-slate-100">
                      {s.image && <img src={s.image} alt="" className="h-full w-full object-cover" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-display text-base font-bold text-navy">{s.title || t("common.rooms")}</div>
                      <div className="text-sm text-slate-500">{s.city} · {formatPrice(s.price)}</div>
                      <div className="mt-1 flex items-center gap-2 text-sm font-medium text-brandblue">
                        <CalendarClock size={14} /> {fmtDate(v.scheduled_at)}
                        {v.agent_name && <span className="text-slate-400">· {v.agent_name}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${st.cls}`}>{st.label}</span>
                      <Select value={v.status} onValueChange={(val) => setStatus(v.id, val)}>
                        <SelectTrigger className="w-[140px] rounded-xl" data-testid={`viewing-status-${v.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(STATUS).map(([k, val]) => <SelectItem key={k} value={k}>{val.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Button variant="outline" size="icon" className="rounded-full" onClick={() => ical(v.id)} data-testid={`viewing-ical-${v.id}`} title=".ics">
                        <Download size={16} />
                      </Button>
                      <Button variant="outline" size="icon" className="rounded-full text-danger" onClick={() => remove(v.id)} data-testid={`viewing-delete-${v.id}`}>
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                  {v.notes && <p className="mt-3 rounded-xl bg-slate-50 px-4 py-2 text-sm text-slate-600">{v.notes}</p>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Bezichtigingen;
